import Vue from 'vue';
import { defineStore } from 'pinia';
import _ from 'lodash';
import { getUnixTime } from 'date-fns';

import i18n from '@/i18n';
import { switchValidatorLocale } from '@/validator';

import ClipboardApi from '@/store/api/system/clipboard';
import JobObservationApi from '@/store/api/system/jobObservation';
import StatusLogApi from '@/store/api/system/statusLog';
import SetupApi from '@/store/api/system/setup';
import UserApi from '@/store/api/system/user';
import AuthApi from '@/store/api/system/auth';
import CmsApi from '@/store/api/cms/cms';

import { default as router, resetRouter } from '@/router';
import { getSettingValues } from '@/helpers';
import echo from '@/echo';

import { Notification } from 'element-ui';

// Import auth store for cross-store communication
import { useAuthStore } from './auth';
import { usePresenceStore } from './presence';

export const useSetupStore = defineStore('setup', {
    state: () => ({
        maintenance: false,
        ready: false,
        loaded: false,
        animation: null,
        paginationFilterDebounce: 600,
        i18n: [
            { value: 'de', label: 'German', flag: 'de' },
            { value: 'en', label: 'English', flag: 'gb' },
        ],
        libretranslate: [],
        clipboard: {},
        locale: 'de',
        activeCms: {},
        activeBreakpoint: undefined,
        visibleLogNotifications: [],
        cmss: [],
        uploadEndpoint: _.trim(`${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_UPLOAD_ROUTE}`, '/'),
    }),

    getters: {
        debounce: (state) => state.paginationFilterDebounce,
        localeObject: (state) => state.i18n.find((el) => el.value === state.locale) || state.i18n[0],
        hasAnyCms: (state) => state.cmss.length > 0,
        enabledExtensions: (state) => {
            const supportedExtensions = _.get(state.activeCms, 'config.template.extensions') || [];
            const activeExtensions = (_.get(state.activeCms, 'extensions') || []).map((extension) => extension.key);
            // 'status-logs', 'communication' and 'tickets' are universal (system_wide)
            // features — available to every storefront regardless of what the template
            // declares.
            return ['status-logs', 'communication', 'tickets', ...supportedExtensions].filter((extension) =>
                activeExtensions.includes(extension)
            );
        },
        // ALL extensions active on the CMS, unfiltered by the template. System-wide
        // extensions (e.g. ai-image-edit, product-video) are admin-only and never
        // listed in config.template.extensions, so enabledExtensions can't see them.
        activeExtensions: (state) => (_.get(state.activeCms, 'extensions') || []).map((extension) => extension.key),
        // The knowledge base is shared: booking the chatbot or KI-Inhalte
        // (ai-content, system-wide) builds the same one, and its settings
        // are reachable with either.
        hasKnowledgeBase() {
            return this.enabledExtensions.includes('chatbot') || this.activeExtensions.includes('ai-content');
        },
        // What the store can actually DO, as opposed to which rows it was sold.
        //
        // Some extensions are contained in another and are therefore never attached
        // in their own right: enabling `shop` removes `restricted-area`, because a
        // shop carries the whole restricted-area functionality and billing it twice
        // would be wrong (the wizard says so on the row: "Enthalten in Shop"). A gate
        // asking "can this store restrict a page to members?" must read THIS, or it
        // silently answers no on every shop.
        //
        // Gates that mean the extension row itself - billing, provisioning, which
        // permissions tab to render - keep using enabledExtensions.
        effectiveExtensions: (state) => _.get(state.activeCms, 'effectiveExtensionKeys') || [],
        enabledFeatures: (state) => _.get(state.activeCms, 'config.template.features') || [],
        settingValues: (state) => {
            const settingDefaults = _.get(state.activeCms, 'config.template.settings') || [];
            const customSettings = _.get(state.activeCms, 'settings') || [];
            return getSettingValues(settingDefaults, customSettings);
        },
    },

    actions: {
        // Lifecycle actions
        heartbeat() {
            // Placeholder for future use
        },

        async checkHealth() {
            const response = await SetupApi.checkHealth();
            if (response.status === 200 && this.maintenance !== false) {
                this.updateMaintenanceState(false);
                router.go(router.currentRoute);
            }
        },

        async initializeI18n() {
            const code = SetupApi.getLocale();
            this.switchI18n(code);

            const libreTranslateEndpoint = `${process.env.VUE_APP_LIBRE_TRANSLATE_HOST}:${process.env.VUE_APP_LIBRE_TRANSLATE_PORT}${process.env.VUE_APP_LIBRE_TRANSLATE_LANGUAGES_ROUTE}`;

            try {
                const response = await fetch(libreTranslateEndpoint);
                this.libretranslate = await response.json();
            } catch (error) {
                console.error('Failed to fetch libretranslate languages:', error);
            }
        },

        // forceCmsId: open a SPECIFIC shop rather than the account's first one —
        // used when a support session was started for one particular website.
        async fetchSetup(ignoreCmsID = false, forceCmsId = null) {
            this.initializeClipboard();
            this.initializeI18n();

            let cmsID;
            if (forceCmsId) {
                this.activeCms = {};
                SetupApi.setActiveCmsId(forceCmsId);
                cmsID = forceCmsId;
            } else if (ignoreCmsID === true) {
                // Clear stale CMS state so addSetupToState aligns activeCms
                // with the administrator the backend returns (first verified one)
                this.activeCms = {};
                SetupApi.removeActiveCmsId();
            } else {
                cmsID = _.get(this, 'activeCms.id') || SetupApi.getActiveCmsId();
            }

            this.stopWebsocketListeners({ user: false, cms: true });

            try {
                const { data } = await SetupApi.fetchSetup(cmsID);
                this.addSetupToState(data);

                try {
                    echo.initialize();
                    this.initializeWebsocketListeners({ user: true, cms: true });
                } catch (error) {
                    console.error(error);
                }

                this.fetchStatusLogNotifications();
            } catch (err) {
                this.fetchFailed(err);
            }
        },

        // Whether a broadcast belongs to the CMS currently open. Events carrying no cms_id
        // are treated as belonging to it, so a worker on the previous release still
        // notifies instead of falling silent.
        belongsToActiveCms(cmsId) {
            if (!cmsId) return true;

            return parseInt(cmsId) === parseInt(_.get(this, 'activeCms.id'));
        },

        async showStatusLogNotification(log) {
            if (!log || !log.notify) return;
            if (this.visibleLogNotifications.includes(parseInt(log.id))) return;

            this.addVisibleLogNotification(parseInt(log.id));

            const markAsAcknowledged = async (logItem) => {
                const logCopy = _.cloneDeep(logItem);
                logCopy.acknowledged_by_user_at = getUnixTime(new Date());

                await StatusLogApi.update(logCopy);
                this.removeVisibleLogNotification(parseInt(logItem.id));
            };

            let link;
            let message;
            let handleClick;

            if (log.file) {
                const file = log.file;
                let storageRoute = _.trim(
                    `${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PRIVATE_STORAGE_ROUTE}`,
                    '/'
                );
                const table = 'files';
                const token = AuthApi.getToken();
                link = `${storageRoute}/${table}/${file.id}/${encodeURIComponent(file.name)}?download&token=${token}`;

                handleClick = `
                    const token = localStorage.getItem('token');
                    event.target.href = event.target.href.replace(/token=[^&]+/, 'token=' + token);
                `
                    .replace(/[\r\n\s]+/g, ' ')
                    .trim();

                message = `<a href="${link}" onclick="${handleClick}" download>${i18n.t('Download file')}</a>`;
            }

            switch (log.key) {
                case 'export-stock-to-csv':
                    if (link) {
                        message = `<a href="${link}" onclick="${handleClick}" download>${i18n.t('Download CSV')}</a>`;
                    }

                    if (log.status === 'success') {
                        Notification.success({
                            title: i18n.t('Stock was successfully exported'),
                            dangerouslyUseHTMLString: true,
                            message,
                            onClose: () => markAsAcknowledged(log),
                            iconClass: 'fas el-icon-fa-check',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 0,
                        });
                    } else if (log.status === 'error') {
                        Notification.error({
                            title: i18n.t('Stock export has failed'),
                            dangerouslyUseHTMLString: true,
                            message,
                            onClose: () => markAsAcknowledged(log),
                            iconClass: 'fas el-icon-fa-xmark',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 0,
                        });
                    }
                    break;

                case 'export-sales-to-csv':
                    if (link) {
                        message = `<a href="${link}" onclick="${handleClick}" download>${i18n.t('Download CSV')}</a>`;
                    }

                    if (log.status === 'success') {
                        Notification.success({
                            title: i18n.t('Sales were successfully exported'),
                            dangerouslyUseHTMLString: true,
                            message,
                            onClose: () => markAsAcknowledged(log),
                            iconClass: 'fas el-icon-fa-check',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 0,
                        });
                    } else if (log.status === 'error') {
                        Notification.error({
                            title: i18n.t('Sales export has failed'),
                            dangerouslyUseHTMLString: true,
                            message,
                            onClose: () => markAsAcknowledged(log),
                            iconClass: 'fas el-icon-fa-xmark',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 0,
                        });
                    }
                    break;

                default:
                    if (log.status === 'success') {
                        Notification.success({
                            title: i18n.t('{job} completed', { job: log.heading }),
                            dangerouslyUseHTMLString: true,
                            message,
                            onClose: () => markAsAcknowledged(log),
                            iconClass: 'fas el-icon-fa-check',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 0,
                        });
                    } else if (log.status === 'error') {
                        Notification.error({
                            title: i18n.t('{job} failed', { job: log.heading }),
                            dangerouslyUseHTMLString: true,
                            message,
                            onClose: () => markAsAcknowledged(log),
                            iconClass: 'fas el-icon-fa-xmark',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 0,
                        });
                    }
                    break;
            }
        },

        async fetchStatusLogNotifications() {
            const authStore = useAuthStore();

            const handleStatusLog = async (log) => {
                await new Promise((resolve) => setTimeout(resolve, 10));
                this.showStatusLogNotification(log);
            };

            const user = _.get(authStore, 'user.id');
            const cms = _.get(this, 'activeCms.id');

            const variables = {
                cms: parseInt(cms),
                user: parseInt(user),
                notify: true,
                acknowledged: false,
                status: ['success', 'error'],
            };

            try {
                const statusLogs = await StatusLogApi.fetchList(variables);
                if (statusLogs && statusLogs.length > 0) {
                    statusLogs.forEach(handleStatusLog);
                }
            } catch (error) {
                console.error('Failed to fetch status log notifications:', error);
            }
        },

        clearCacheForCms(cms) {
            return CmsApi.clearCache(cms);
        },

        copyCmsSettings({ source, target, includeImages = false }) {
            return CmsApi.copySettings(source, target, includeImages);
        },

        /**
         * Store one value at `path` in the person's own settings and save them. The
         * value must also be selected in UserSettings.gql, or the next unrelated
         * settings save writes the whole object back without it and erases it.
         */
        async saveUserSetting({ path, value }) {
            const authStore = useAuthStore();
            const settings = _.cloneDeep(_.get(authStore, 'user.settings') || {});

            _.set(settings, path, value);
            authStore.updateUserSettings(settings);

            const id = _.get(authStore, 'user.id');
            if (id) {
                const result = await UserApi.update({ id, settings });
                authStore.updateUserSettings(_.get(result, 'settings'));
            }
        },

        saveActiveColumnKeys({ scope, key, data }) {
            return this.saveUserSetting({ path: `${scope}.pagination.${key}.activeColumnKeys`, value: data });
        },

        saveFilterVisibility({ scope, key, value }) {
            return this.saveUserSetting({ path: `${scope}.pagination.${key}.showFilters`, value });
        },

        /**
         * How a surface groups its rows - today only the ticket timeline, which
         * can stack its bars by project, assignee, status or customer. Same
         * storage as the column selection and the filter visibility: the
         * person's settings, not the desk's.
         *
         * The value must also be selected in UserSettings.gql, or the next
         * unrelated settings save writes the whole object back without it and
         * silently erases the choice.
         */
        saveGroupBy({ scope, key, value }) {
            return this.saveUserSetting({ path: `${scope}.pagination.${key}.groupBy`, value });
        },

        /**
         * The user's own saved filter sets for one list. Same storage as the
         * column selection and the filter visibility — the person's settings,
         * not the desk's — so every paginated surface can offer presets.
         */
        saveFilterPresets({ scope, key, presets }) {
            return this.saveUserSetting({ path: `${scope}.pagination.${key}.filterPresets`, value: presets });
        },

        async saveUserSettingsActivePaginationChildren({ key, value }) {
            const authStore = useAuthStore();

            let settings = _.get(authStore, 'user.settings', {});
            if (!settings) settings = {};
            else settings = _.cloneDeep(settings);

            _.set(settings, `co.pagination.${key}.activeChildren`, value);
            authStore.updateUserSettings(settings);

            const id = _.get(authStore, 'user.id');
            if (id) {
                try {
                    const result = await UserApi.update({ id, settings });
                    const updatedSettings = _.get(result, 'settings');
                    authStore.updateUserSettings(updatedSettings);
                } catch (err) {
                    console.error(err);
                }
            }
        },

        async saveMiscSettings({ key, value }) {
            const authStore = useAuthStore();

            let settings = _.get(authStore, 'user.settings', {});
            if (!settings) settings = {};
            else settings = _.cloneDeep(settings);

            const misc = _.get(settings, 'misc') || [];
            let i = misc.findIndex((item) => item && item.key === key);

            if (i < 0) misc.push({ key, value });
            else misc[i] = { key, value };

            settings.misc = misc.filter(Boolean);
            authStore.updateUserSettings(settings);

            const id = _.get(authStore, 'user.id');
            if (id) {
                const result = await UserApi.update({ id, settings });
                const updatedSettings = _.get(result, 'settings');
                authStore.updateUserSettings(updatedSettings);
            }
        },

        activateTab(item) {
            if (item && item.path) {
                return router.push(item.path);
            }
        },

        initializeWebsocketListeners({ user, cms }) {
            const authStore = useAuthStore();
            const id = _.get(authStore, 'user.id');

            if (user && id) {
                echo.client
                    .private('user.' + id)
                    .listen('.inventory-protocol:created', (data) => {
                        // This channel carries the user scope only - see the comment on
                        // the status-log listener below. The protocol belongs to the
                        // stock's CMS, so it is only announced there.
                        if (!this.belongsToActiveCms(_.get(data, 'cms_id'))) return;

                        Notification.success({
                            title: i18n.t('pdf.inventoryProtocolCreated'),
                            iconClass: 'fas el-icon-fa-check',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000,
                        });
                    })
                    .listen('.status-log:updated', (data) => {
                        // Scope to cms + user, exactly like fetchStatusLogNotifications().
                        // This channel carries the user scope only, so for an account
                        // holding several CMS a job finishing in one of them would
                        // otherwise pop its toast in whatever CMS is currently open.
                        // Nothing is lost by dropping it here: the log stays
                        // unacknowledged and the fetch on the next CMS switch shows it in
                        // its own CMS.
                        if (!this.belongsToActiveCms(_.get(data, 'log.cms_id'))) return;

                        if (
                            data.log &&
                            !data.log.acknowledged_by_user_at &&
                            ['success', 'error'].includes(data.log.status)
                        ) {
                            this.showStatusLogNotification(data.log);
                        }
                    });
            }

            if (cms && this.activeCms && this.activeCms.id) {
                echo.client.private('cms.' + this.activeCms.id).listen('.job:updated', (data) => {
                    const allowed = [
                        'summarize:site-view-statistics',
                        'summarize:page-view-statistics',
                        'summarize:sales-statistics',
                    ];
                    if (data.job && allowed.includes(data.job.type)) {
                        this.updateUnfinishedJob(data.job);
                    }
                });
            }
        },

        stopWebsocketListeners({ user, cms }) {
            const authStore = useAuthStore();
            const id = _.get(authStore, 'user.id');

            if (user && id) {
                echo.client
                    .private('user.' + id)
                    .stopListening('.inventory-protocol:created')
                    .stopListening('.status-log:updated')
                    .stopListening('.job:updated');
            }

            if (cms && this.activeCms && this.activeCms.id) {
                echo.client.private('cms.' + this.activeCms.id).stopListening('.job:updated');
            }
        },

        // Mutations converted to actions
        switchI18n(code) {
            this.locale = code;
            i18n.locale = code;
            switchValidatorLocale(code);
            SetupApi.setLocale(code);
        },

        setLibreTranslate(data) {
            this.libretranslate = data;
        },

        initializeClipboard() {
            const clipboard = ClipboardApi.get() || {};
            this.clipboard = clipboard;
        },

        setClipboard({ key, context = 'cms', value }) {
            const clipboard = ClipboardApi.set(key, value, context) || {};
            this.clipboard = clipboard;
        },

        removeFromClipboard(data) {
            let clipboard;
            if (typeof data === 'string') {
                const key = data;
                clipboard = ClipboardApi.remove(key) || {};
            } else {
                const { key, context = 'cms' } = data;
                clipboard = ClipboardApi.remove(key, context) || {};
            }
            this.clipboard = clipboard;
        },

        addSetupToState(data) {
            const authStore = useAuthStore();

            this.loaded = true;
            Vue.set(this, 'meta', data.CO_Setup.meta);

            if (data.CO_Setup.auth) {
                authStore.addAuthenticatedUserToState(data.CO_Setup.auth);
                authStore.addAuthenticatedAdministratorToState(data.CO_Setup.administrator);
                // Whether this browser is inside a support session, and who
                // really entered, is the server's word - never a local copy.
                authStore.setImpersonation(data.CO_Setup.impersonation);

                const administrators = _.get(data.CO_Setup.auth, 'administrators') || [];
                const allCmss = administrators.map((admin) => _.get(admin, 'cms')).filter(Boolean);

                this.cmss = allCmss;

                if (allCmss.length) {
                    const activeCmsId = _.get(this.activeCms, 'id') || SetupApi.getActiveCmsId();
                    if (activeCmsId) {
                        const activeCms = allCmss.find((cms) => parseInt(cms.id) === parseInt(activeCmsId));
                        if (activeCms) this.activeCms = activeCms;
                        else this.activeCms = _.head(allCmss);
                    } else {
                        this.activeCms = _.head(allCmss);
                    }

                    const breakpoints = _.get(this.activeCms, 'config.template.breakpoints') || [];
                    this.activeBreakpoint =
                        breakpoints.find((el) => el.default) || breakpoints[Math.max(breakpoints.length - 1, 0)];

                    SetupApi.setActiveCmsId(this.activeCms.id);
                } else {
                    this.activeCms = {};
                    this.activeBreakpoint = null;
                    SetupApi.removeActiveCmsId();
                }
            }
        },

        // Take a freshly saved copy of the store the app chrome is already rendering.
        // Without this, everything read off activeCms - the missing-sender banner, the
        // enabled extensions, the store's name in the switcher - keeps showing the state
        // the page was loaded with until a full reload. Deliberately NOT a switch:
        // presence, the stored active id and the breakpoint stay exactly as they are.
        refreshActiveCms(cms) {
            const activeCmsId = parseInt(_.get(this, 'activeCms.id'));
            if (!cms || !cms.id || !activeCmsId) return;
            if (parseInt(cms.id) !== activeCmsId) return;

            this.activeCms = cms;

            const index = this.cmss.findIndex((entry) => parseInt(entry.id) === activeCmsId);
            if (index !== -1) Vue.set(this.cmss, index, cms);
        },

        updateActiveCms(cms) {
            // Leave presence on the CURRENT (old) cms before switching, so our "in use"
            // badge clears immediately on other clients instead of waiting out the TTL.
            usePresenceStore().leaveAll();

            this.activeCms = cms;
            SetupApi.setActiveCmsId(cms.id);

            const breakpoints = _.get(this.activeCms, 'config.template.breakpoints') || [];
            this.activeBreakpoint =
                breakpoints.find((el) => el.default) || breakpoints[Math.max(breakpoints.length - 1, 0)];
        },

        updateBreakpoint(value) {
            this.activeBreakpoint = value;
        },

        updateReadyState(ready) {
            this.ready = ready;
        },

        updateLoadedState(loaded) {
            this.loaded = loaded;
        },

        updateAnimation(animation) {
            this.animation = animation;
        },

        updateMaintenanceState(maintenance) {
            this.maintenance = maintenance;
        },

        addVisibleLogNotification(id) {
            this.visibleLogNotifications.push(id);
        },

        removeVisibleLogNotification(id) {
            this.visibleLogNotifications = this.visibleLogNotifications.filter((el) => el !== id);
        },

        fetchFailed(err) {
            throw err;
        },

        addOptionsToSectionSettings(data) {
            const section = this.activeCms.config.template.sections[data.sectionIndex];
            if (section && section.settings && section.settings[data.settingIndex]) {
                section.settings[data.settingIndex].options = data.options;
            }
        },

        updateUnfinishedJob(job) {
            // Placeholder for job updates - currently commented out in original
        },

        hideUnfinishedJob(id) {
            localStorage.setItem(`job:${id}`, 'hidden');
        },

        removeUnfinishedJob(id) {
            localStorage.setItem(`job:${id}`, 'deleted');
        },
    },
});
